/**
 * DocsAndExpensesPanel — file PMS fusionnée Documents clients + Dépenses immeubles.
 * Batch #14.153 — UI carte unifiée avec icônes contextuelles selon le type.
 * Batch #14.154 — extracted from AdminPmsQueuePage.jsx during refactor.
 * Batch #14.221 — sous-composants extraits dans ./docsAndExpenses/ (oversized refactor).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Loader2, FolderArchive } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "./_shared";
import { ExpenseCard } from "./docsAndExpenses/ExpenseCard";
import { DropCard } from "./docsAndExpenses/DropCard";
import { AdminExpenseModal } from "./docsAndExpenses/AdminExpenseModal";

export const EXPENSE_CATEGORIES = [
  { value: "entretien_reparation",    label: "Entretien / Réparation" },
  { value: "renovations",              label: "Rénovations" },
  { value: "taxes_municipales",        label: "Taxes municipales" },
  { value: "taxes_scolaires",          label: "Taxes scolaires" },
  { value: "assurance_immeuble",       label: "Assurance immeuble" },
  { value: "assurance_responsabilite", label: "Assurance responsabilité" },
  { value: "energie_chauffage",        label: "Énergie / Chauffage" },
  { value: "electricite_communes",     label: "Électricité communes" },
  { value: "eau_aqueduc",              label: "Eau / Aqueduc" },
  { value: "internet_telecom",         label: "Internet / Télécom" },
  { value: "concierge_menage",         label: "Concierge / Ménage" },
  { value: "deneigement_paysagement",  label: "Déneigement / Paysagement" },
  { value: "extermination",            label: "Extermination" },
  { value: "frais_juridiques",         label: "Frais juridiques" },
  { value: "frais_comptables",         label: "Frais comptables" },
  { value: "frais_administration",     label: "Frais d'administration" },
  { value: "frais_bancaires_stripe",   label: "Frais bancaires / Stripe" },
  { value: "interets_hypotheque",      label: "Intérêts hypothèque" },
  { value: "publicite_marketing",      label: "Publicité / Marketing" },
  { value: "petits_outils",            label: "Petits outils" },
  { value: "fournitures_bureau",       label: "Fournitures bureau" },
  { value: "autre",                    label: "Autre" },
];

export default function DocsAndExpensesPanel({ onUpdate }) {
  const [activeView, setActiveView] = useState("all"); // all | expenses | drops
  const [showCreate, setShowCreate] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [dropStatus, setDropStatus] = useState("pending");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (yearFilter) params.set("year", String(yearFilter));
      const [rE, rD] = await Promise.all([
        axios.get(`${API}/admin/pms/expenses?${params}`, { headers: authH() }),
        axios.get(`${API}/admin/pms/client-drops?status=${dropStatus}`, { headers: authH() }),
      ]);
      setExpenses(rE.data?.items || []);
      setDrops(rD.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [yearFilter, dropStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const items = (() => {
    const list = [];
    if (activeView === "all" || activeView === "expenses") {
      for (const e of expenses) {
        list.push({ _type: "expense", _sortKey: e.invoice_date || e.created_at || "", data: e });
      }
    }
    if (activeView === "all" || activeView === "drops") {
      for (const d of drops) {
        list.push({ _type: "drop", _sortKey: d.created_at || "", data: d });
      }
    }
    list.sort((a, b) => (b._sortKey || "").localeCompare(a._sortKey || ""));
    return list;
  })();

  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount_total || 0), 0);

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("Archiver cette dépense ?")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/pms/expenses/${id}`, { headers: authH() });
      toast.success("Dépense archivée");
      await loadAll();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const handleOpenDrop = async (doc) => {
    if (doc._kind === "safe" && doc.drive_web_view_link) {
      window.open(doc.drive_web_view_link, "_blank", "noopener,noreferrer");
      return;
    }
    setBusyId(doc.id);
    try {
      const r = await axios.get(`${API}/admin/pms/client-drops/${doc.id}/download`, {
        headers: authH(),
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: doc.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (doc._kind === "safe" && doc.drive_web_view_link) {
        window.open(doc.drive_web_view_link, "_blank", "noopener,noreferrer");
      } else {
        toast.error(e.response?.data?.detail || "Impossible d'ouvrir le document");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleDownloadDrop = async (doc) => {
    if (doc._kind === "safe" && doc.drive_web_view_link) {
      window.open(doc.drive_web_view_link, "_blank", "noopener,noreferrer");
      return;
    }
    setBusyId(doc.id);
    try {
      const r = await axios.get(`${API}/admin/pms/client-drops/${doc.id}/download`, {
        headers: authH(),
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: doc.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.name || "document";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Téléchargement impossible");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleProcessed = async (doc) => {
    setBusyId(doc.id);
    try {
      await axios.put(`${API}/admin/pms/client-drops/${doc.id}/processed`, {}, { headers: authH() });
      toast.success(doc.is_processed ? "Marqué comme à traiter" : "Marqué comme traité ✓");
      await loadAll();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteDrop = async (doc) => {
    if (!window.confirm(`Supprimer définitivement "${doc.original_filename || doc.name}" ?`)) return;
    setBusyId(doc.id);
    try {
      await axios.delete(`${API}/admin/pms/client-drops/${doc.id}`, { headers: authH() });
      toast.success("Document supprimé");
      await loadAll();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-CA", { year: "numeric", month: "short", day: "numeric" });
    } catch { return String(iso).slice(0, 10); }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  };

  return (
    <>
      <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-2" data-testid="docs-expenses-panel">
        <div className="flex items-center gap-2 flex-wrap">
          <FolderArchive className="w-4 h-4 text-rose-600" />
          <h2 className="font-semibold text-slate-800 text-sm">Documents clients & Dépenses immeubles</h2>
          {expenses.length > 0 && (
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white border text-slate-700">
              Dépenses {yearFilter} : <b>{totalExpenses.toFixed(2)} $</b>
            </span>
          )}
          {drops.length > 0 && (
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white border text-slate-700">
              Documents : <b>{drops.length}</b>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAll}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 disabled:opacity-40"
            data-testid="docs-expenses-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
          {/* Batch #14.210 — Bouton "Saisir une dépense" retiré (gestion dans Immeuble) */}
        </div>
      </div>

      <div className="px-5 py-2.5 border-b bg-white flex items-center gap-2 flex-wrap">
        {[
          { id: "all", label: `Tous (${expenses.length + drops.length})`, tone: "slate" },
          { id: "expenses", label: `Dépenses (${expenses.length})`, tone: "rose" },
          { id: "drops", label: `Documents (${drops.length})`, tone: "indigo" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveView(f.id)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition ${
              activeView === f.id
                ? f.tone === "rose"
                  ? "bg-rose-600 text-white border-rose-600"
                  : f.tone === "indigo"
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={`docs-expenses-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {(activeView === "all" || activeView === "expenses") && (
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(Number(e.target.value))}
              className="text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white"
              data-testid="docs-expenses-year"
            >
              {[0, 1, 2].map((delta) => {
                const y = new Date().getFullYear() - delta;
                return <option key={y} value={y}>Dépenses {y}</option>;
              })}
            </select>
          )}
          {(activeView === "all" || activeView === "drops") && (
            <select
              value={dropStatus}
              onChange={(e) => setDropStatus(e.target.value)}
              className="text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white"
              data-testid="docs-expenses-drop-status"
            >
              <option value="pending">Docs à traiter</option>
              <option value="processed">Docs traités</option>
              <option value="all">Tous docs</option>
            </select>
          )}
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200" data-testid="docs-expenses-empty">
            <FolderArchive className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              {activeView === "expenses" && `Aucune dépense pour ${yearFilter}.`}
              {activeView === "drops" && `Aucun document client.`}
              {activeView === "all" && `Aucune dépense ni document client à afficher.`}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="docs-expenses-list">
            {items.map((it) => it._type === "expense"
              ? <ExpenseCard
                  key={`exp-${it.data.id}`}
                  exp={it.data}
                  busy={busyId === it.data.id}
                  onDelete={() => handleDeleteExpense(it.data.id)}
                  fmtDate={fmtDate}
                />
              : <DropCard
                  key={`drop-${it.data.id}`}
                  doc={it.data}
                  busy={busyId === it.data.id}
                  onOpen={() => handleOpenDrop(it.data)}
                  onDownload={() => handleDownloadDrop(it.data)}
                  onToggle={() => handleToggleProcessed(it.data)}
                  onDelete={() => handleDeleteDrop(it.data)}
                  fmtDate={fmtDate}
                  fmtSize={fmtSize}
                />
            )}
          </div>
        )}
      </div>

      {showCreate && (
        <AdminExpenseModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadAll(); onUpdate?.(); }}
        />
      )}
    </>
  );
}
